// Client-side defense-in-depth for inline TABLE candidate previews.
//
// The server already sanitizes table content twice (jsoup strict allowlist at
// ingest AND at serialize). This re-scrubs the HTML IN THE BROWSER before it is
// injected, mirroring the server allowlist, so that no server regression — or a
// row that somehow bypassed it — can ever put executable markup on the page.
//
// DOMParser("text/html") builds an INERT document: it does not run scripts or
// load resources, so parsing hostile markup here is itself safe; we then keep
// only whitelisted table tags/attributes and drop everything else.

const ALLOWED_TAGS = new Set([
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD", "CAPTION",
  "COLGROUP", "COL", "BR", "B", "I", "EM", "STRONG", "SUB", "SUP", "SPAN", "P",
]);

// Per-tag attribute allowlist (structural table attrs only — no style/class/id/
// href/src/on*). Everything not listed here is stripped.
const ALLOWED_ATTRS = {
  TH: ["colspan", "rowspan", "scope"],
  TD: ["colspan", "rowspan"],
  COL: ["span"],
  COLGROUP: ["span"],
};

// Elements dropped ENTIRELY (with their content), never unwrapped, if present.
const DROP_WITH_CONTENT = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META",
  "SVG", "MATH", "NOSCRIPT", "TEMPLATE", "IMG", "AUDIO", "VIDEO", "SOURCE",
]);

function scrub(parent) {
  // Snapshot children first — we mutate the tree as we go.
  Array.prototype.slice.call(parent.childNodes).forEach((node) => {
    if (node.nodeType === 3) {
      return; // text node — inert, keep
    }
    if (node.nodeType !== 1) {
      node.remove(); // comments / anything non-element → drop
      return;
    }
    // Drop any non-HTML-namespace element (SVG / MathML / foreignObject) ENTIRELY.
    // Foreign elements have case-preserving (lowercase) tagName so an uppercase
    // tag-name check misses them, and they are a classic mXSS vector — never valid
    // table content. A namespace check also covers camelCase SVG like foreignObject.
    if (node.namespaceURI !== "http://www.w3.org/1999/xhtml") {
      node.remove();
      return;
    }
    const tag = node.tagName;
    if (DROP_WITH_CONTENT.has(tag)) {
      node.remove();
      return;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      // Disallowed but not dangerous → unwrap: clean its subtree, then hoist its
      // children in place of the wrapper so legitimately-wrapped tables survive.
      scrub(node);
      while (node.firstChild) {
        parent.insertBefore(node.firstChild, node);
      }
      node.remove();
      return;
    }
    const allowed = ALLOWED_ATTRS[tag] || [];
    Array.prototype.slice.call(node.attributes).forEach((attr) => {
      if (allowed.indexOf(attr.name.toLowerCase()) === -1) {
        node.removeAttribute(attr.name);
      }
    });
    scrub(node);
  });
}

// Return an XSS-safe HTML string (table tags + light emphasis only) for the given
// server-sanitized table HTML. Null/empty in → "" out. Idempotent.
export function sanitizeTableHtml(html) {
  if (!html) {
    return "";
  }
  const doc = new DOMParser().parseFromString(
    `<body><div id="__tbl">${html}</div></body>`, "text/html");
  const root = doc.getElementById("__tbl");
  scrub(root);
  return root.innerHTML;
}
